import { z } from "zod";

import api from "./base";
import { codecGet, dualKey, isObject, toCamelDeep } from "@/api/codec";

// ---------------------------------------------------------------------------
// Phase 3 codec migration (see ``api/codec.ts``): every READ fetcher in
// this module now rides ``codecGet`` — the global axios camelizer is
// skipped (``rawResponse: true``) and a per-endpoint Zod schema parses
// the verbatim snake_case body into the SAME camelCase envelope the
// consumers have always seen. Two consequences:
//
//   1. Code-ID-keyed records (``codes`` / ``penaltyPerCode`` /
//      ``assignments`` / ``confidenceBand``) arrive with their snake
//      keys INTACT on the raw path — the old ``restoreCodeIdKeys``
//      un-camelize shim is gone. The schemas still run those keys
//      through ``decamelizeKey`` (below) so a payload that rode the
//      legacy camelizer (e.g. a cached fixture) collapses back onto
//      the snake truth. NOTE: ``codec.toSnake`` is deliberately NOT
//      used for these keys — it treats digits as word boundaries
//      ("elevator_cat1" → "elevator_cat_1", "ll97_emissions" →
//      "ll_9_7_emissions"), which would corrupt verbatim snake keys
//      on the raw path. ``decamelizeKey`` only splits on uppercase
//      letters (humps semantics), so it's idempotent on the BE truth.
//   2. The schemas are transport-agnostic (``dualKey`` reads snake
//      first, camel fall-back), so MSW fixtures authored in either
//      spelling parse identically.
// ---------------------------------------------------------------------------

/**
 * Per-code renderability flag emitted by the BE manifest
 * (``tracking/services_code_manifest.py::can_render``). Drives the
 * dashboard row's per-cell render decision in Phase 3.A:
 *
 *   - ``canRender: true``  → cell renders its real value.
 *   - ``canRender: false`` + ``fallbackCta``: cell renders em-dash plus
 *     a CTA link wired to the Phase-3.C smart-confirmation dialog.
 *   - ``canRender: false`` + ``fallbackCta: null``: cell renders em-dash
 *     only (no actionable CTA — happens for codes that gate on external
 *     city data the user can't supply).
 *
 * Manifest keys deliberately match the BE-side manifest (LL-numbered
 * + a handful of inspection codes). Keys are stable; if the BE adds
 * a code it'll appear here automatically — the FE only special-cases
 * ``ll97Emissions`` (em-dash + CTA in the penalty cell). Other codes
 * always render today; their ``canRender`` is informational and feeds
 * the row-level "City estimates" badge.
 */
export interface CodeRenderabilityEntry {
  canRender: boolean;
  fallbackCta: string | null;
}

export interface CodeRenderability {
  ll97Emissions?: CodeRenderabilityEntry;
  ll88Lighting?: CodeRenderabilityEntry;
  ll126Facade?: CodeRenderabilityEntry;
  ll152Gas?: CodeRenderabilityEntry;
  elevator?: CodeRenderabilityEntry;
  boiler?: CodeRenderabilityEntry;
  dobNowSafety?: CodeRenderabilityEntry;
}

export interface PortalBuildingMetrics {
  id: string;
  name: string;
  address: string;
  bin: string;
  penaltyExposure: number;
  overdueFilingsCount: number;
  upcomingFilingsCount: number;
  complianceScore: number;
  /**
   * DEPRECATED in Phase 3.A for RENDERABILITY: the row-wide "data
   * missing" cascade is retired, and the dashboard reads per-cell
   * ``codeRenderability`` for cell-level rendering decisions instead.
   *
   * STILL READ, THOUGH — do not delete it. It is half of the
   * "this figure understates" verdict on both surfaces that show the
   * enforcement number: ``Dashboard/Page.tsx`` (``rowUnderstated``) and
   * the building page's header rail
   * (``BuildingDetail/header.ts`` ``isExposureUnderstated``). An earlier
   * version of this comment said the field had no consumers; it had one
   * at the time and has two now.
   */
  penaltyDataMissing?: boolean;
  /**
   * NEW (Phase 3.A): per-code renderability decisions from the BE
   * manifest. Optional during the overlap window with Phase 3.A.BE;
   * when absent, the FE falls back to rendering every cell (i.e.
   * assumes ``canRender === true`` and no badge).
   */
  codeRenderability?: CodeRenderability;
  /**
   * Per-row identity hints from the BE so the dashboard's "Confirm
   * details to estimate" CTA can prefill the smart-confirmation
   * dialog without round-tripping through the per-building codes
   * endpoint. Populated by ``portal_views._compute_building_metrics``
   * from the Phase 2.A ``Building.effective_*()`` cascade (DB-first;
   * falls through to PLUTO/CBL/legacy without live SODA calls).
   * Wire shape is snake_case on the BE; the axios camelizer rewrites
   * to these names.
   */
  bbl?: string | null;
  grossSqft?: number | null;
  yearBuilt?: number | null;
  primaryFunction?: string | null;
  postalCode?: string | null;
  /**
   * Claude Design 4-state chip + ``~`` prefix driver. Derived BE-side
   * from the same ``effective_*()`` cascade as the identity hints
   * above, so a single dashboard read carries both the values and the
   * trust signal. Optional during the BE overlap window — when absent,
   * the FE falls back to inferring a tier from ``codeRenderability``
   * (the legacy hint surface).
   *
   *   verified       — every identity field is owner-attested
   *   needs_review   — at least one field is missing or legacy-backfilled
   *   syncing        — created in the last 5 minutes, no owner trust yet
   *   city_estimate  — has data, but at least one field came from PLUTO/CBL
   *
   * See ``backend/server/tracking/portal_views.py::_compute_building_data_state``
   * for the canonical derivation.
   */
  dataState?: "verified" | "needs_review" | "syncing" | "city_estimate";
  /**
   * PR-A/PR-B (FR-1/7/13) — enforcement/projection split. The blended
   * ``penaltyExposure`` above is kept during the rollout (I3: these two
   * sum to it exactly). ``enforcementExposure`` = open city penalties
   * already docketed (OATH all-agency + DOB/ECB/DOB NOW/boiler, floored
   * at 0/building); ``codeProjectionExposure`` = forward LL97/LL88/LL84
   * statutory estimate not yet assessed. Optional during the BE overlap
   * window — when absent, the FE treats the whole ``penaltyExposure`` as
   * enforcement (pre-split behavior).
   */
  enforcementExposure?: number;
  /**
   * ``null`` when the building's read FAILED and no code was priced: the cell
   * draws an em-dash, because "we could not read it" is not a $0 estimate.
   */
  codeProjectionExposure?: number | null;
  /**
   * The read that failed, verbatim off ``get_building_tracking``. The row's
   * money is what we COULD read (enforcement and interest resolve off
   * snapshots), so this is the third state: not $0, not verified.
   */
  trackingError?: string | null;
  /**
   * PR-A (FR-10/11) — server-shipped per-building risk verdict. Replaces
   * the FE ``riskTierFromExposure`` brackets (deleted in PR-C). Vocabulary
   * differs from the FE ``RiskPill`` tier ("med"/"ok") — this is the
   * canonical server enum.
   */
  serverRiskTier?: ServerRiskTier;
  /**
   * PR-A (Part 2 item 4) — honesty flag: the all-agency OATH enforcement
   * snapshot for this building is missing/errored, so
   * ``enforcementExposure`` understates and must NOT be shown as an
   * authoritative number.
   */
  enforcementCoverageMissing?: boolean;
  /**
   * PR-8 — the move-never-delete attribution split for THIS building's OATH
   * matter ledger. Same primitive, same dollars and the SAME wire key as the
   * building page's ``/violation-matters/`` response, so a row chip and the
   * page it opens can never disagree.
   *
   * NOT A PARTITION OF ``enforcementExposure`` — see
   * ``PortalAttributionSplit`` for why, and
   * ``components/portal/portalRowAttribution.ts`` for the render rule that
   * keeps it from being drawn as one.
   *
   * Gated server-side on the ``violation_remediation`` flag and stamped only
   * on the PAGINATED rows shape — absent on the unparameterized whole-
   * portfolio shape the chat picker and login warm-up ride.
   */
  attributionSplit?: PortalAttributionSplit;
  /**
   * PR-8 — how much of this building's owed money has sat with no future date
   * for more than ``edgeDays``. Attribution-AGNOSTIC and bounded by
   * ``attributionSplit.compositeOathOwed``, which is what ``basis`` says on
   * the wire. Same gating as ``attributionSplit``.
   */
  unresolved90dPlus?: PortalUnresolved90dPlus;
  /** Matters on this building, counted server-side over the same queryset the
   *  Record head totals. The Record roll-up's SUMMONSES column. */
  summonsesCount?: number;
}

/**
 * PR-A (FR-10/11) — canonical server risk-tier enum. Distinct from the
 * FE ``RiskPill`` tier union ("high"|"med"|"ok"|"none"); the server
 * speaks "medium"/"compliant" and adds "unknown" for buildings whose
 * enforcement coverage is missing (never mis-counted as compliant).
 */
export type ServerRiskTier = "high" | "medium" | "compliant" | "unknown";

/** One bucket of {@link PortalAttributionSplit}. Amounts are decimal STRINGS. */
export interface PortalAttributionBucket {
  /** Decimal string, not a number — the BE ships ``str(Decimal)``. */
  amount: string;
  count: number;
  /** Present on the two exception buckets only. */
  posture?: string;
  /** ``prior_owner`` only; ``null`` until counsel approves the copy. */
  verifyCopy?: string | null;
}

/**
 * PR-8 / FR-14 — the per-building attribution split.
 *
 * ── THE ONE THING A RENDERER MUST NOT DO WITH THIS ────────────────────────
 *
 * These dollars are bounded by ``compositeOathOwed`` — the OATH/ECB summons
 * subset — NOT by the row's ``enforcementExposure``, which is the whole
 * composite (ECB + all-agency OATH + DOB NOW Safety + DOB Boiler). A building
 * can show ``enforcementExposure`` well above ``compositeOathOwed``; the
 * difference is boiler / elevator / DOB-NOW money with no summons matter
 * behind it. That is the correct reading, not a leak.
 *
 * So ``yourEntities + underReview + notYours`` does NOT add to the OATH
 * column, and nothing on screen may imply that it does: no "of", no
 * percentage, no subtraction, and the chips never sit in a money column.
 * ``portalRowAttribution.ts`` owns that rule and a test pins it.
 *
 * ``priorOwner`` is a SUBSET of ``underReview`` (not a fourth disjoint
 * bucket) whose dollars stay INSIDE ``owedTotal`` — verify, not ignore.
 *
 * ``matterReadErrors`` non-zero means every total here is a FLOOR, not a
 * measurement.
 */
export interface PortalAttributionSplit {
  /** The customer-facing owed number after move-never-delete: your + review. */
  owedTotal: string;
  yourEntities: PortalAttributionBucket;
  underReview: PortalAttributionBucket;
  notYours: PortalAttributionBucket;
  /** Subset of ``underReview``; its dollars are already inside ``owedTotal``. */
  priorOwner: PortalAttributionBucket;
  /** The full OATH subset the split partitions. NOT ``enforcementExposure``. */
  compositeOathOwed: string;
  matterReadErrors: number;
  buildingId?: string;
  asOf?: string | null;
}

/**
 * PR-8 — the per-building 90-day-plus aging fact.
 *
 * ATTRIBUTION-AGNOSTIC, exactly as the portfolio aging block is: a ticket
 * whose respondent is a confirmed third party still ages on the city's
 * ledger. So a row can legitimately read "NOT YOURS $X" and count that same
 * ticket here — one ticket, counted once under each of two different
 * questions (whose money, and how old). The two must never be drawn as two
 * disjoint piles that add up.
 *
 * ``basis`` is on the wire precisely so a renderer cannot miss it: these
 * dollars are bounded by ``attributionSplit.compositeOathOwed``, not by the
 * row's ``enforcementExposure``.
 */
export interface PortalUnresolved90dPlus {
  count: number;
  /** Decimal string. */
  amountUsd: string;
  /** The aging edge in days — 90 today, read rather than assumed. */
  edgeDays: number;
  /** e.g. ``"oath_matters_default_composite"``. */
  basis: string;
}

/**
 * PR-8 — per-tier DOLLARS beside the per-tier counts, PORTFOLIO-SCOPED.
 *
 * THE SCOPE RULING, on the wire: band dollars are portfolio-wide
 * (``scope: "portfolio"``); row counts are page-scoped (the rows envelope
 * carries ``scope: "page"``). Before this existed the FE summed the rows it
 * happened to be holding, which is a page, not a portfolio — so page 1 of a
 * paginated portfolio printed a page subtotal under a portfolio hero.
 *
 * ``totals`` is the sum of the four tiers and equals the dashboard hero
 * exactly; it ships so a consumer can check the foot WITHOUT re-adding the
 * bands. Nothing here is recomputed on the client.
 *
 * ``unknown`` is a REAL BAND carrying both a count and dollars — an
 * unrecognized tier folds into it rather than being dropped, so the bands
 * always foot.
 */
export interface PortalRiskTierBand {
  count: number;
  penaltyUsd: number;
  enforcementUsd: number;
  codeProjectionUsd: number;
}

export interface PortalRiskTierExposure {
  scope: "portfolio";
  buildingsCount: number;
  tiers: Record<ServerRiskTier, PortalRiskTierBand>;
  totals: {
    penaltyUsd: number;
    enforcementUsd: number;
    codeProjectionUsd: number;
  };
}

/**
 * PR-A (FR-12) — per-portfolio risk-tier distribution, derived server-side
 * from the SAME per-building ``serverRiskTier`` each row ships (one
 * formula). Powers the dashboard's segmented health bar + rail headline.
 * ``unknown`` counts buildings with missing enforcement coverage.
 */
export interface RiskTierDistribution {
  high: number;
  medium: number;
  compliant: number;
  unknown: number;
}

/**
 * Wave-15 Track B (FE-stub here, BE owner): per-compliance-tier counts
 * for the dashboard's segmented health bar. Replaces the legacy client-
 * side derivation that mapped per-building `penaltyExposure` thresholds
 * to A/B/C/D/F tiers — the BE now owns this aggregation so the dashboard
 * stays correct when the buildings list is paginated (the FE would only
 * see the current page, never the whole portfolio).
 *
 * Per `feedback_one_formula_per_user_visible_number`: one source of
 * truth for the tier distribution, server-computed across all buildings
 * the user can see — not derived from a 10-row slice.
 */
export interface ComplianceTierDistribution {
  // Keys LOWERCASE — humps.camelizeKeys (axios response interceptor)
  // lowercases single-letter uppercase keys, so the BE ships lowercase to
  // match. See feedback_camelcase_axios_jsonfield.
  a: number;
  b: number;
  c: number;
  d: number;
  f: number;
}

/**
 * CPLR 5004 post-judgment interest, portfolio-wide. Decimal STRINGS: quoted to
 * a lender and a float rounds it. NOT a slice of `penaltyExposure`, never added
 * to it (Leo, 2026-09-24): unstated it hid $17,798.96 of Related's OATH debt,
 * 17.5%. `judgmentCount` excludes a judgment whose docket date has not arrived.
 */
export interface PortalJudgmentInterest {
  totalUsd: string;
  principalUsd: string;
  judgmentCount: number;
  annualRate: string;
  /** Principal x 9% / 12, whole dollars. Absent on a pre-key payload. */
  perMonthUsd?: string;
}

/** `enforcement_by_agency`: DOB + other == `enforcement_exposure` to the cent,
 *  or null when the server could not foot them. */
export interface PortalEnforcementByAgency {
  dobUsd: number;
  otherAgencyUsd: number;
}

export interface PortalDashboardData {
  penaltyExposure: number;
  /** ABSENT IS NOT ZERO: a payload predating the BE key renders no line. */
  judgmentInterest?: PortalJudgmentInterest;
  /** Existing — kept for backward-compat; ignored in Portal V1 dashboard. */
  overdueFilingsCount: number;
  /** Existing — kept for backward-compat; ignored in Portal V1 dashboard. */
  upcomingFilingsCount: number;
  /** Existing — kept for backward-compat; ignored in Portal V1 dashboard. */
  complianceScore: number;
  buildingsCount: number;
  /**
   * NEW (Portal V1, Track A): count of buildings where penalty_exposure > 0.
   * Optional during the brief overlap window with the backend roll-out;
   * the FE falls back to deriving from `buildingsSummary` when absent.
   */
  exposedBuildingsCount?: number;
  /** NEW (Portal V1, Track A): count of buildings missing a penalty estimate. */
  penaltyDataMissingCount?: number;
  /**
   * Buildings whose read FAILED. Their code-driven estimate is not inside the
   * totals on this payload, so the count is the disclosure: the masthead
   * states it rather than printing an exact-looking portfolio figure over
   * fewer buildings than the customer owns.
   */
  unreadableBuildingsCount?: number;
  /**
   * PR-B FR-35 — portfolio-level estimated (pending-confirmation) exposure,
   * reported separately from the booked headline. Optional during the BE
   * roll-out overlap; the dashboard hero renders no caveat when absent.
   */
  penaltyExposureEstimated?: number;
  /**
   * PR-A/PR-B (FR-1/13) — portfolio enforcement/projection split totals.
   * Hero PRIMARY reads ``enforcementExposure``; SECONDARY reads
   * ``codeProjectionExposure``. I3: the two sum to ``penaltyExposure``.
   * Optional during BE overlap — the hero falls back to treating the
   * whole ``penaltyExposure`` as enforcement when absent.
   */
  enforcementExposure?: number;
  codeProjectionExposure?: number;
  /**
   * PR-A (FR-12) — per-portfolio risk-tier distribution
   * {high, medium, compliant, unknown}, derived from the same
   * per-building ``serverRiskTier``. Powers the segmented health bar +
   * rail headline. Preferred over the legacy A-F
   * ``complianceTierDistribution`` when present.
   */
  riskTierDistribution?: RiskTierDistribution;
  /**
   * PR-8 — per-tier DOLLARS beside ``riskTierDistribution``'s counts,
   * PORTFOLIO-scoped, with a ``totals`` foot that equals the hero. This is
   * the ONLY honest source for a band subtotal once the rows are paginated;
   * summing the visible rows yields a page subtotal. Optional during the BE
   * overlap window — absent means the bands render their counts and state
   * that the dollars are unavailable, never a summed-from-rows figure.
   */
  riskTierExposure?: PortalRiskTierExposure;
  /**
   * PR-A (Part 2) — portfolio-level honesty flags. ``enforcementCoverage
   * Missing`` / ``penaltyDataMissing`` are surfaced by the FE as a subtle
   * "data incomplete — enforcement may be understated" state rather than
   * an authoritative number. Optional; the FE also derives an understated
   * signal from ``riskTierDistribution.unknown`` / ``penaltyDataMissing
   * Count`` during the overlap window.
   */
  enforcementCoverageMissing?: boolean;
  penaltyDataMissing?: boolean;
  /**
   * Wave-15 Track B — server-side compliance tier distribution for the
   * dashboard's segmented bar. Optional during the brief overlap window
   * with the BE PR landing on Track B; the FE renders nothing for the
   * segmented bar when absent (the legacy client-side derivation off
   * `buildingsSummary` is gone — a paginated list can't compute the
   * portfolio-wide distribution honestly).
   */
  complianceTierDistribution?: ComplianceTierDistribution;
  /**
   * Wave-15 Track B — total BIN count across the entire portfolio
   * (NOT the current page). Surfaced in the dashboard eyebrow so users
   * see "N buildings" even when the buildings list pagination only has
   * 10 rows on screen. Optional during BE rollout overlap.
   */
  totalBinCount?: number;
  /**
   * LEGACY (Wave-15 Track B removes from BE response): full buildings
   * list embedded in the dashboard payload. The new flow reads this
   * from `usePortalBuildingsQuery({ page, pageSize })` so dashboards
   * scale past 10–20 buildings without shipping every row to the client.
   *
   * Marked optional so the FE compiles against both pre- and post-Track-B
   * BE responses. Once the BE PR is fully rolled out, this field can be
   * deleted along with its few remaining read sites.
   */
  buildingsSummary?: PortalBuildingMetrics[];
  /**
   * NEW: snapshot-freshness disclosure. ``asOf`` is the ISO-8601
   * timestamp of the MOST RECENT BuildingDataSnapshot row for any
   * tracked building (across all sources). ``null`` when zero
   * snapshots exist yet for the company's portfolio.
   *
   * Powers the "Snapshot data refreshed X ago" line under the hero
   * $ number on /portal. Optional during the brief BE rollout overlap
   * window — older payloads omit it and the FE renders nothing.
   */
  asOf?: string | null;
  /** Server verdict on `asOf`. The rail reads it, never computes it. */
  asOfIsFresh?: boolean;
  /**
   * NEW: paired with ``asOf`` — counts the portfolio's freshness
   * distribution so the FE can render "M of N buildings fresh"
   * when not all buildings have a <24h snapshot.
   *
   * ``freshBuildings`` counts buildings whose most-recent snapshot
   * across all sources is younger than 24h. See
   * ``backend/server/tracking/portal_views.py::_build_dashboard_freshness``
   * for the canonical definition.
   */
  freshnessSummary?: {
    totalBuildings: number;
    freshBuildings: number;
  };
}

export interface PortalBuildingsData {
  buildings: PortalBuildingMetrics[];
  /**
   * Wave-15 Track B — pagination envelope. Optional during the BE
   * rollout overlap window; older payloads omit these fields and the
   * FE falls through to a single-page render (no pagination UI).
   *
   * `page` is 1-indexed to match the URL semantics. `total` is the
   * count BEFORE pagination is applied (NOT `buildings.length`).
   */
  page?: number;
  pageSize?: number;
  total?: number;
  totalPages?: number;
  /**
   * PR-8 — the scope of the ROW figures, stated rather than inferred. These
   * rows are ONE PAGE, so summing any per-row figure yields a page subtotal.
   * It describes ``buildings`` only: ``total`` / ``totalPages`` beside it stay
   * portfolio counts, and the portfolio-scoped band DOLLARS live on the
   * dashboard's ``riskTierExposure`` (``scope: "portfolio"``).
   *
   * Absent on the unparameterized whole-portfolio shape, where the rows ARE
   * the filtered portfolio and there is no page to scope to.
   */
  scope?: "page";
}

/**
 * Wave-15 Track B — params for the paginated buildings endpoint.
 * All optional; the BE defaults to `page=1, pageSize=10` and no filter.
 *
 * Wave-16 — ``q`` is the portfolio-wide search term. Per
 * ``feedback_address_search_through_bin`` the BE resolves it through
 * BIN (7-digit fast-path OR Geosearch cross-walk), never icontains
 * against stored rows. Empty/undefined ``q`` returns the full
 * portfolio.
 */
/**
 * Buildings-table sort contract (feat/portal-enforcement-split). The BE
 * accepts exactly these three sort keys + two directions on
 * ``GET /api/tracking/portal/buildings/``; anything else falls back to the
 * default (``enforcement`` / ``desc``) server-side and still 200s. Kept as
 * named unions so the route ``validateSearch``, the table header, and the
 * mobile Sort control all speak one vocabulary.
 *
 *   - ``enforcement`` — OATH column (booked city penalties)
 *   - ``projection``  — Code-Driven column (forward statutory estimate)
 *   - ``risk``        — RISK column (server risk tier; desc = high-first)
 */
export type PortalBuildingsSortBy = "enforcement" | "projection" | "risk";
export type PortalBuildingsSortDir = "asc" | "desc";

export interface PortalBuildingsParams {
  page?: number;
  pageSize?: number;
  q?: string;
  /**
   * Server-side sort key + direction. Optional so unpaginated callers
   * (chat sidebar / WelcomeWarmup dedup) keep the legacy cacheable shape
   * (no ``sort_by``/``sort_dir`` on the wire). The dashboard always
   * forwards both, defaulting to the server default (``enforcement`` /
   * ``desc``) so the first paint matches the BE without a re-sort.
   */
  sortBy?: PortalBuildingsSortBy;
  sortDir?: PortalBuildingsSortDir;
}

export interface CodeActionState {
  kind: "scheduled";
  date: string;
  vendor: string;
}

/**
 * Per-code BE classification with reasoning chain. LL152 today;
 * other booking-style obligations (LL11 / LL126 / BPP_BOILER) light
 * up by adding their wrapper to ``tracking.services``.
 *
 * Wire shape mirrors ``tracking.subcycle::SubcycleExplanation``:
 *   - ``sub_window``:  "A" | "B" | "C" | "D" | "UNKNOWN" | "NOT_APPLICABLE"
 *   - ``cycle_period``: "2027-2030" etc., empty for sentinels
 *   - ``deadline``:    ISO yyyy-mm-dd, empty for sentinels
 *   - ``due_status``:  "not_yet_due" | "upcoming" | "in_grace" |
 *                      "overdue" | "data_unavailable" | "not_applicable"
 *   - ``steps``:       reasoning chain — one entry per material
 *                      decision in the classification (PLUTO,
 *                      RCNY 103-10, calendar derivation).
 */
export interface CodeSubcycleStep {
  fact: string;
  source: string;
  citation: string;
}

export interface CodeSubcycle {
  subWindow: string;
  cyclePeriod: string;
  deadline: string;
  dueStatus: string;
  steps: CodeSubcycleStep[];
}

export interface CodeResult {
  /**
   * `unknown` is the fail-safe state for upstream-data outages
   * (PLUTO 5xx/timeout, schema drift, missing required field) —
   * distinct from `not_eligible`, which is authoritative. See
   * docs/chat-assistant/research/10-tracking-failsafe-architecture.md
   * for the rationale (FN-5 fix, Phase A).
   *
   * `mediated_resolution` is the LL97 Article 321 (CP3) pathway —
   * the customer is eligible to file via the mediated/prescriptive
   * route. Added 2026-05-23 (commit 9130d2be) and gated as eligible
   * by the building-detail "Start filing" CTA (see Page.tsx).
   */
  status:
    | "eligible"
    | "not_eligible"
    | "maybe"
    | "unknown"
    | "mediated_resolution";
  reason: string;
  eligible: boolean | null;
  needsOwnerInput?: boolean;
  ownerQuestionKey?: string;
  deadline?: string | null;
  schedule?: string | null;
  actionState?: CodeActionState | null;
  subcycle?: CodeSubcycle | null;
  /** C7 record fields, server-composed per request (`services/code_record.py`).
   *  Optional: a pre-C7 payload carries none of them. */
  label?: string;
  effectiveDue?: string | null;
  effectiveDueBasis?: "missed_cycle" | "overdue_since" | "deadline" | null;
  /** Every Codes-tab amount is a projection today; `null` = no amount,
   *  absent = pre-C7 payload (read as estimate). */
  levyState?: "estimate" | null;
  ticketIds?: string[];
  /** Shown as text; `recordUrl` is set only for a human city page. */
  recordId?: string | null;
  recordUrl?: string | null;
}

export interface PortalBuildingSubmission {
  id: string;
  status: number;
  calendarYear: number;
}

export interface PortalLL87Submission {
  id: string;
  status: string;
  cycleYear: number;
}

// ---------------------------------------------------------------------
// Phase 3.D — data lineage projection.
//
// The BE returns `data_lineage` (snake_case on the wire); the axios
// camelizer rewrites it to `dataLineage` here. Inner JSONB keys are
// ALSO camelized (so `current_value` -> `currentValue`,
// `previous_entry` -> `previousEntry`, etc.) — see
// frontend/app/src/api/base.ts top-of-file CTO note.
//
// Entry MAY be absent for a field whose cascade returned None — the
// consumer should null-guard each key.
// ---------------------------------------------------------------------

export interface DataLineagePreviousEntry {
  source: string | null;
  attestedAt: string | null;
  valueSnapshot: number | string | null;
}

// Phase 4 — multi-attestation history. The BE dual-emits the legacy
// `previous_entry` (singular) for back-compat AND `previous_entries`
// (plural, ordered oldest-first); the FE prefers plural when non-empty.
export interface DataLineageHistoryEntry {
  source: string | null;
  attestedAt: string | null;
  valueSnapshot: number | string | null;
  confidence: "high" | "medium" | "low" | null;
  supersededAt: string | null;
}

export interface DataLineageFieldEntry {
  currentValue: number | string | null;
  currentSource: string | null;
  currentAttestedAt: string | null;
  confidence: "high" | "medium" | "low";
  previousEntry: DataLineagePreviousEntry | null;
  previousEntries: DataLineageHistoryEntry[];
  persistedValue: number | string | null;
}

export interface DataLineage {
  grossFloorArea?: DataLineageFieldEntry;
  primaryFunction?: DataLineageFieldEntry;
  yearBuilt?: DataLineageFieldEntry;
  postalCode?: DataLineageFieldEntry;
}

/**
 * One section of the code board, as the SERVER orders and counts it.
 *
 * Mirrors a `categories[]` entry of `backend/server/tracking/constants/
 * code_taxonomy.py` `build_code_taxonomy` — read that docstring, it is the
 * shape contract. Three things about it are load-bearing here:
 *
 *   * `codeIds` are the codes of this category ON THIS PAYLOAD, not every
 *     code the category declares. A category whose checker raised is SHORT
 *     here and named in `PortalCodeTaxonomy.missing`.
 *   * `total` is the section header's denominator, and the block `total`
 *     the footer states is the sum of these. The board renders THIS, never
 *     `codeIds.length` — the server happens to set them equal today
 *     (`_section`: `"total": len(code_ids)`), and a header that counts the
 *     array it was handed is a second formula waiting for the day it
 *     doesn't.
 *   * `applicable` is the server's `status_bucket(...) == "eligible"` count
 *     — the same formula as the board's `statusToPillBucket`, which the
 *     backend's `FrontendVocabularyParityTests` pins equal in both
 *     directions. It is the section header's "N APPLICABLE".
 *   * `projectedPenalty` is the `> 0` slice of `penalty_per_code`, i.e. the
 *     per-category breakdown of `penalty_breakdown.code_driven.total` — NOT
 *     of `code_projection_exposure` and NOT of `penaltyExposureEstimated`.
 */
export interface PortalCodeTaxonomySection {
  id: string;
  label: string;
  codeIds: string[];
  total: number;
  applicable: number;
  projectedPenalty: number;
}

/**
 * The `/codes/` payload's code taxonomy — section order, per-section
 * rollups, and the block rollup the board's footer states.
 *
 * WHY THE BLOCK FIGURES ARE READ AND NEVER RECOMPUTED. The server builds
 * `total` / `applicable` / `projectedPenalty` AS THE SUMS of `categories`,
 * so the summary line, every section header and the board footer reconcile
 * by construction rather than by three call sites agreeing. Deriving any of
 * them in the browser would be a second formula for a user-visible number.
 *
 * TWO FIELDS THAT ARE NOT DECORATION:
 *
 *   * `uncategorized` — codes on the payload with no category. The server
 *     ALSO appends them as a real trailing entry in `categories` (same
 *     keys), so the board draws them with the same component and the block
 *     sums still cover every row. Empty on every healthy payload; a code
 *     that lands there is a defect the owner should see as a row, never as
 *     a vanished obligation. This id list is the same set, for reporting.
 *   * `missing` — tracked codes the payload does NOT carry, because
 *     `process_building` catches only `TypeError` and any other raise
 *     leaves the key unset. Without surfacing it the board prints a
 *     perfectly plausible "5 OF 30" while three obligations were never
 *     checked, so `CodesBoardFooter` states it on the face.
 */
export interface PortalCodeTaxonomy {
  categories: PortalCodeTaxonomySection[];
  total: number;
  applicable: number;
  projectedPenalty: number;
  uncategorized: string[];
  missing: string[];
}

export interface PortalCodeBucketCounts {
  eligible: number;
  maybe: number;
  unknown: number;
  notEligible: number;
}

export interface PortalBuildingCodesData extends PortalBuildingMetrics {
  /** CPLR 5004 interest, never in a total. Absent on a pre-key payload. */
  judgmentInterest?: PortalJudgmentInterest;
  enforcementByAgency?: PortalEnforcementByAgency | null;
  /** `judgmentInterest` by agency; the halves sum to it. */
  judgmentInterestByAgency?: {
    dob: PortalJudgmentInterest;
    otherAgency: PortalJudgmentInterest;
  } | null;
  /** Every portfolio BIN on the building, primary first. */
  bins?: string[];
  codes: Record<string, CodeResult>;
  penaltyPerCode: Record<string, number>;
  /**
   * PR-5 — the server's code vocabulary + rollups. OPTIONAL, and the
   * optionality is real: a backend mid-deploy (or a payload replayed from
   * before PR-3) carries no `code_taxonomy`, and the board falls back to
   * the `CODE_GROUPS` vocabulary and states no counts. See
   * `pages/Portal/BuildingDetail/codesBoard.ts` `deriveCodeBoard`.
   */
  codeTaxonomy?: PortalCodeTaxonomy;
  /** `code_taxonomy.bucket_counts`, the filter pills. Read apart from the
   *  all-or-nothing taxonomy; absent on a pre-C7 payload. */
  codeBucketCounts?: PortalCodeBucketCounts;
  grossSqft: number | null;
  numBuildings: number | null;
  borough: string | null;
  bbl: string | null;
  ll97Submissions: PortalBuildingSubmission[];
  ll88Submissions: PortalBuildingSubmission[];
  ll87Submissions: PortalLL87Submission[];
  /**
   * Phase 3.D — Tracker-only multi-attestation provenance trail. The
   * filing-app endpoint does NOT include this field; only
   * `/api/tracking/portal/buildings/<id>/codes/` does.
   */
  dataLineage?: DataLineage;
  /**
   * Wave-3 W-int-A — per-code confidence band. Keys are code IDs
   * (snake_case, e.g. "ll97_emissions"); values follow the snake_case
   * ``ConfidenceBandShape`` in ``pages/Portal/BuildingDetail/codeView.ts``
   * (``confidence_label`` / ``cure_window`` / ``rate_source``). Typed
   * loosely here so the api layer doesn't import a component type;
   * BuildingDetail/Page.tsx narrows it at the read site (its
   * historical localized cast keeps working). Declared as part of the
   * Phase 3 codec migration — the codec drops undeclared fields, and
   * this one is load-bearing for the confidence chips.
   */
  confidenceBand?: Record<string, unknown>;
  /**
   * PR-B FR-35 — estimated-vs-booked split. `penaltyBreakdown.code_driven.
   * estimated_pending_confirmation` carries {total, count, caveats} for the
   * accruing-exposure hero caveat; snake_case inner keys are part of the BE
   * contract, so the sub-tree rides `decamelizeKeysDeep` like
   * `confidenceBand`. Load-bearing for the detail-hero caveat — the codec
   * drops undeclared fields.
   */
  penaltyBreakdown?: Record<string, unknown>;
  /** PR-B FR-35 — building-level estimated (pending-confirmation) exposure. */
  penaltyExposureEstimated?: number;
}

// ---------------------------------------------------------------------------
// Codecs (Phase 3 — see the module-header note). All schemas are
// tolerant/additive like the interfaces above: optional fields stay
// optional, unknown extra wire fields drop (typed ownership), and no
// field is coerced beyond what the legacy camelize path delivered.
// ---------------------------------------------------------------------------

/**
 * humps-style decamelize: insert ``_`` before UPPERCASE letters only.
 * Unlike ``codec.toSnake`` (which also splits on digits and would
 * corrupt verbatim code IDs — see the module-header note), this is
 * idempotent on the BE's snake truth: "elevator_cat1" and
 * "fc905121" round-trip unchanged, while a legacy-camelized
 * "elevatorCat1" / "ll97Emissions" collapses back to the snake key
 * ``CODE_GROUPS`` matches against.
 */
const decamelizeKey = (s: string): string =>
  s.replace(/([A-Z])/g, (_, c: string) => `_${c.toLowerCase()}`);

/** Deep counterpart for ``confidenceBand`` (snake-canonical sub-tree
 *  whose TOP-level keys are code IDs — digit-safe rewrite required). */
const decamelizeKeysDeep = (input: unknown, depth = 0): unknown => {
  if (depth > 6) return input;
  if (Array.isArray(input)) {
    return input.map((v) => decamelizeKeysDeep(v, depth + 1));
  }
  if (isObject(input)) {
    return Object.entries(input).reduce<Record<string, unknown>>(
      (acc, [k, v]) => {
        acc[decamelizeKey(k)] = decamelizeKeysDeep(v, depth + 1);
        return acc;
      },
      {},
    );
  }
  return input;
};

/**
 * Re-key a code-ID-keyed record onto canonical snake_case keys, mapping
 * each value through ``mapValue``. On the raw path the keys arrive
 * correct ("hpd_registration") and ``decamelizeKey`` is a no-op; on a
 * legacy camelized payload ("hpdRegistration") it collapses them back
 * onto the snake truth that ``CODE_GROUPS`` matches against.
 */
const readCodeKeyedRecord = <T>(
  raw: unknown,
  mapValue: (v: unknown) => T,
): Record<string, T> | undefined => {
  if (!isObject(raw)) return undefined;
  return Object.entries(raw).reduce<Record<string, T>>((acc, [key, value]) => {
    acc[decamelizeKey(key)] = mapValue(value);
    return acc;
  }, {});
};

/**
 * Parse one building-metrics row (``portal_views._compute_building_metrics``
 * wire dict) into the camelCase ``PortalBuildingMetrics`` contract.
 * Inner camelCase sub-trees (``codeRenderability``) ride ``toCamelDeep``;
 * everything else is a ``dualKey`` field read. Tolerant of the BE's
 * tracking-error degraded rows (which omit most fields).
 */
const readBuildingMetrics = (
  raw: Record<string, unknown>,
): PortalBuildingMetrics => {
  const renderability = dualKey(raw, "code_renderability");
  return {
    id: dualKey(raw, "id") as string,
    name: dualKey(raw, "name") as string,
    address: dualKey(raw, "address") as string,
    bin: dualKey(raw, "bin") as string,
    penaltyExposure: dualKey(raw, "penalty_exposure") as number,
    overdueFilingsCount: dualKey(raw, "overdue_filings_count") as number,
    upcomingFilingsCount: dualKey(raw, "upcoming_filings_count") as number,
    complianceScore: dualKey(raw, "compliance_score") as number,
    penaltyDataMissing: dualKey(raw, "penalty_data_missing") as
      | boolean
      | undefined,
    codeRenderability: (isObject(renderability)
      ? toCamelDeep(renderability)
      : undefined) as CodeRenderability | undefined,
    bbl: dualKey(raw, "bbl") as string | null | undefined,
    grossSqft: dualKey(raw, "gross_sqft") as number | null | undefined,
    yearBuilt: dualKey(raw, "year_built") as number | null | undefined,
    primaryFunction: dualKey(raw, "primary_function") as
      | string
      | null
      | undefined,
    postalCode: dualKey(raw, "postal_code") as string | null | undefined,
    dataState: dualKey(raw, "data_state") as
      | PortalBuildingMetrics["dataState"]
      | undefined,
    // PR-A/PR-B — enforcement/projection split + server risk verdict.
    enforcementExposure: dualKey(raw, "enforcement_exposure") as
      | number
      | undefined,
    codeProjectionExposure: dualKey(raw, "code_projection_exposure") as
      | number
      | null
      | undefined,
    trackingError: dualKey(raw, "tracking_error") as string | null | undefined,
    // A CAST, NOT A NARROWING — any string the BE emits flows through, and
    // the codec is one deploy away from the BE at all times. Every consumer
    // folds an unrecognised value into ``unknown`` rather than trusting this
    // type: ``portalRiskBands.normaliseTier`` (band headers, page counts,
    // contiguity) and ``riskPillTierFromServer``'s ``default`` branch (the
    // row pill). Narrowing here instead would only move the fold, not remove
    // the need for one, and would throw away the raw value a future triage
    // may want.
    serverRiskTier: dualKey(raw, "risk_tier") as ServerRiskTier | undefined,
    enforcementCoverageMissing: dualKey(raw, "enforcement_coverage_missing") as
      | boolean
      | undefined,
    // PR-8 — attribution + aging. Both are nested sub-trees, so they ride
    // ``toCamelDeep`` like ``code_renderability`` rather than a flat
    // ``dualKey`` read. ``unresolved_90d_plus`` camelizes to
    // ``unresolved90dPlus`` with a LOWERCASE d (humps folds the ``d`` into the
    // number segment) — ``dualKey``'s ``toCamel`` agrees, so both spellings
    // resolve. Absent (flag off, or the unparameterized rows shape) stays
    // undefined: the chips then render nothing rather than a fabricated $0.
    attributionSplit: (isObject(dualKey(raw, "attribution_split"))
      ? toCamelDeep(dualKey(raw, "attribution_split"))
      : undefined) as PortalAttributionSplit | undefined,
    unresolved90dPlus: (isObject(dualKey(raw, "unresolved_90d_plus"))
      ? toCamelDeep(dualKey(raw, "unresolved_90d_plus"))
      : undefined) as PortalUnresolved90dPlus | undefined,
    // THIS READER IS AN ALLOW-LIST: a wire key it does not name is DROPPED,
    // silently. `summonses_count` shipped, the Record roll-up read
    // `summonsesCount`, and the column drew a dash on every row because the
    // codec ate the field in between (2026-09-23). Rides BOTH shapes.
    summonsesCount: dualKey(raw, "summonses_count") as number | undefined,
  };
};

const readBuildingMetricsList = (
  raw: unknown,
): PortalBuildingMetrics[] | undefined => {
  if (!Array.isArray(raw)) return undefined;
  return raw.filter(isObject).map((row) => readBuildingMetrics(row));
};

export const portalDashboardSchema: z.ZodType<
  PortalDashboardData,
  z.ZodTypeDef,
  unknown
> = z.unknown().transform((input): PortalDashboardData => {
  const raw = isObject(input) ? input : {};
  const freshness = dualKey(raw, "freshness_summary");
  return {
    penaltyExposure: dualKey(raw, "penalty_exposure") as number,
    // Nested snake inner keys, so `toCamelDeep` and not a flat read.
    judgmentInterest: (isObject(dualKey(raw, "judgment_interest"))
      ? toCamelDeep(dualKey(raw, "judgment_interest"))
      : undefined) as PortalJudgmentInterest | undefined,
    overdueFilingsCount: dualKey(raw, "overdue_filings_count") as number,
    upcomingFilingsCount: dualKey(raw, "upcoming_filings_count") as number,
    complianceScore: dualKey(raw, "compliance_score") as number,
    buildingsCount: dualKey(raw, "buildings_count") as number,
    exposedBuildingsCount: dualKey(raw, "exposed_buildings_count") as
      | number
      | undefined,
    penaltyDataMissingCount: dualKey(raw, "penalty_data_missing_count") as
      | number
      | undefined,
    unreadableBuildingsCount: dualKey(raw, "unreadable_buildings_count") as
      | number
      | undefined,
    penaltyExposureEstimated: dualKey(raw, "penalty_exposure_estimated") as
      | number
      | undefined,
    // PR-A/PR-B — enforcement/projection split + risk-tier distribution +
    // portfolio honesty flags. Inner keys of ``risk_tier_distribution``
    // (high/medium/compliant/unknown) are lowercase words, identical in
    // both spellings — no rewrite needed.
    enforcementExposure: dualKey(raw, "enforcement_exposure") as
      | number
      | undefined,
    codeProjectionExposure: dualKey(raw, "code_projection_exposure") as
      | number
      | undefined,
    riskTierDistribution: dualKey(raw, "risk_tier_distribution") as
      | RiskTierDistribution
      | undefined,
    // PR-8 — per-tier dollars. Nested (``tiers`` / ``totals`` with snake
    // inner keys), so ``toCamelDeep`` rather than a flat read. Tier NAMES
    // (high/medium/compliant/unknown) are lowercase words, identical in both
    // spellings; only ``penalty_usd`` & co. need rewriting.
    riskTierExposure: (isObject(dualKey(raw, "risk_tier_exposure"))
      ? toCamelDeep(dualKey(raw, "risk_tier_exposure"))
      : undefined) as PortalRiskTierExposure | undefined,
    enforcementCoverageMissing: dualKey(raw, "enforcement_coverage_missing") as
      | boolean
      | undefined,
    penaltyDataMissing: dualKey(raw, "penalty_data_missing") as
      | boolean
      | undefined,
    // Keys are single lowercase letters (a/b/c/d/f) — identical in both
    // spellings, no rewrite needed.
    complianceTierDistribution: dualKey(raw, "compliance_tier_distribution") as
      | ComplianceTierDistribution
      | undefined,
    totalBinCount: dualKey(raw, "total_bin_count") as number | undefined,
    buildingsSummary: readBuildingMetricsList(
      dualKey(raw, "buildings_summary"),
    ),
    asOf: dualKey(raw, "as_of") as string | null | undefined,
    // Allow-list: an unnamed key is dropped silently.
    asOfIsFresh: dualKey(raw, "as_of_is_fresh") as boolean | undefined,
    freshnessSummary: (isObject(freshness)
      ? toCamelDeep(freshness)
      : undefined) as PortalDashboardData["freshnessSummary"],
  };
});

export const fetchPortalDashboard = (): Promise<PortalDashboardData> =>
  codecGet("tracking/portal/dashboard/", portalDashboardSchema);

// ---------------------------------------------------------------------------
// Wave-8 Cluster A — exposure-history series.
//
// Wire shape (camelized by ``api/base`` axios interceptor):
//   - asOf:            ISO-8601 UTC timestamp, or null when the portfolio
//                      has no snapshots yet
//   - monthlyBuckets:  12-entry array, oldest first, last = current month;
//                      each entry { month: "YYYY-MM", totalExposureUsd: number }
//   - baselineUsd:     same as monthlyBuckets[0].totalExposureUsd
//   - currency:        "USD"
//   - historySupported: false in v1 (Option C — flat series; the FE
//                      branches on this to render an honest sparkline +
//                      "history coming soon" affordance rather than a
//                      fake delta line). When v2 lands and per-month
//                      composites are real, this flips to true.
//
// camelCase note (`feedback_camelcase_axios_jsonfield`): every BE field
// here is snake_case on the wire (``total_exposure_usd``, ``as_of``,
// ``monthly_buckets``, ``baseline_usd``, ``history_supported``); the
// axios interceptor rewrites to the names below. ``YYYY-MM`` month
// strings are unchanged (camelizer leaves dash-strings alone).
// ---------------------------------------------------------------------------
export interface PortalExposureHistoryBucket {
  /** ``YYYY-MM`` — locale-agnostic and lexicographically sortable. */
  month: string;
  totalExposureUsd: number;
  /**
   * PR-A/PR-B (FR-4) — enforcement-only bucket value (per-building
   * ``max(0, ·)`` floored, matching the dashboard's
   * ``enforcementExposure``). The hero sparkline plots THIS series, not
   * the blended ``totalExposureUsd``. Optional during BE overlap — the
   * FE falls back to ``totalExposureUsd`` when absent.
   */
  enforcementExposureUsd?: number;
}

export interface PortalExposureHistoryData {
  asOf: string | null;
  monthlyBuckets: PortalExposureHistoryBucket[];
  baselineUsd: number;
  /**
   * PR-A/PR-B (FR-4) — enforcement-only series baseline (first bucket's
   * ``enforcementExposureUsd``). Optional during BE overlap.
   */
  enforcementBaselineUsd?: number;
  currency: "USD";
  /** v1 sentinel — false means the series is flat (current portfolio
   * exposure echoed across all 12 buckets); see backend
   * ``PortalExposureHistoryView`` docstring for v2 plans. */
  historySupported: boolean;
}

export const portalExposureHistorySchema: z.ZodType<
  PortalExposureHistoryData,
  z.ZodTypeDef,
  unknown
> = z.unknown().transform((input): PortalExposureHistoryData => {
  const raw = isObject(input) ? input : {};
  const buckets = dualKey(raw, "monthly_buckets");
  return {
    asOf: dualKey(raw, "as_of") as string | null,
    monthlyBuckets: (Array.isArray(buckets) ? buckets : [])
      .filter(isObject)
      .map((b) => ({
        month: dualKey(b, "month") as string,
        totalExposureUsd: dualKey(b, "total_exposure_usd") as number,
        enforcementExposureUsd: dualKey(b, "enforcement_exposure_usd") as
          | number
          | undefined,
      })),
    baselineUsd: dualKey(raw, "baseline_usd") as number,
    enforcementBaselineUsd: dualKey(raw, "enforcement_baseline_usd") as
      | number
      | undefined,
    currency: dualKey(raw, "currency") as "USD",
    historySupported: dualKey(raw, "history_supported") === true,
  };
});

export const fetchPortalExposureHistory =
  (): Promise<PortalExposureHistoryData> =>
    codecGet(
      "tracking/portal/dashboard/exposure-history/",
      portalExposureHistorySchema,
    );

export const portalBuildingsSchema: z.ZodType<
  PortalBuildingsData,
  z.ZodTypeDef,
  unknown
> = z.unknown().transform((input): PortalBuildingsData => {
  const raw = isObject(input) ? input : {};
  return {
    buildings: readBuildingMetricsList(dualKey(raw, "buildings")) ?? [],
    page: dualKey(raw, "page") as number | undefined,
    pageSize: dualKey(raw, "page_size") as number | undefined,
    total: dualKey(raw, "total") as number | undefined,
    totalPages: dualKey(raw, "total_pages") as number | undefined,
    // PR-8 — "page", or absent on the unparameterized shape. Narrowed rather
    // than cast so a future value the FE does not understand reads as absent
    // (no scope claim) instead of as a false "page" stamp.
    scope: dualKey(raw, "scope") === "page" ? "page" : undefined,
  };
});

export const fetchPortalBuildings = (
  params: PortalBuildingsParams = {},
): Promise<PortalBuildingsData> =>
  codecGet("tracking/portal/buildings/", portalBuildingsSchema, {
    // Snake-case on the wire to match Django's default param style.
    // (Request params still ride the request interceptor's
    // decamelize, but these are authored snake already.)
    params: {
      ...(params.page !== undefined ? { page: params.page } : {}),
      ...(params.pageSize !== undefined ? { page_size: params.pageSize } : {}),
      // Wave-16 — only forward q when present and non-empty so the
      // legacy unfiltered shape stays cacheable.
      ...(params.q ? { q: params.q } : {}),
      // feat/portal-enforcement-split — server-side sort. Only forwarded
      // when the caller opts in; unpaginated dedup callers omit both and
      // ride the legacy unsorted cache slot. Invalid values are coerced
      // to the default server-side (always 200), so no client guard here.
      ...(params.sortBy ? { sort_by: params.sortBy } : {}),
      ...(params.sortDir ? { sort_dir: params.sortDir } : {}),
    },
  });

/**
 * Wave-15 Track B — lightweight "BIN-only" endpoint for the portal
 * layout's portfolio-wide BIN dedup (used by the chat sidebar and
 * cross-page invalidation, NOT this Track-C UI). Kept here only as a
 * type stub so consumers can call it without a TS error during the
 * overlap window; the implementation will land with Track B's BE PR.
 */
export interface PortalBuildingBinsData {
  bins: string[];
}

export const portalBuildingBinsSchema: z.ZodType<
  PortalBuildingBinsData,
  z.ZodTypeDef,
  unknown
> = z.unknown().transform((input): PortalBuildingBinsData => {
  const raw = isObject(input) ? input : {};
  const bins = dualKey(raw, "bins");
  return {
    bins: (Array.isArray(bins) ? bins : []).filter(
      (b): b is string => typeof b === "string",
    ),
  };
});

export const fetchPortalBuildingBins = (): Promise<PortalBuildingBinsData> =>
  codecGet("tracking/portal/building-bins/", portalBuildingBinsSchema);

const readSubmissionList = (raw: unknown): PortalBuildingSubmission[] => {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isObject).map((s) => ({
    id: dualKey(s, "id") as string,
    status: dualKey(s, "status") as number,
    calendarYear: dualKey(s, "calendar_year") as number,
  }));
};

const readLL87SubmissionList = (raw: unknown): PortalLL87Submission[] => {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isObject).map((s) => ({
    id: dualKey(s, "id") as string,
    status: dualKey(s, "status") as string,
    cycleYear: dualKey(s, "cycle_year") as number,
  }));
};

/** A finite number, or `undefined`. `NaN` / `null` / `"12"` are not figures. */
const readFinite = (raw: unknown): number | undefined =>
  typeof raw === "number" && Number.isFinite(raw) ? raw : undefined;

/**
 * A list of code-id strings; anything else in the array is dropped.
 *
 * Used for the two REPORTING lists (`uncategorized`, `missing`), where a
 * dropped entry costs a name in a caveat. The category `code_ids` lists are
 * read strictly instead — see `readCodeTaxonomySection` — because those are
 * the ROWS.
 */
const readIdList = (raw: unknown): string[] =>
  Array.isArray(raw)
    ? raw.filter((v): v is string => typeof v === "string")
    : [];

/**
 * One `categories[]` entry, or `undefined` if it is not well-formed.
 *
 * STRICT ON EVERY FIELD, because a section is not decoration: `codeIds` is
 * the render list and `total` is the denominator its header states — the
 * one the block `total` in the footer is the SUM of. A section defaulting
 * its figures while the block parses off the wire is exactly the
 * "Σ sections ≠ block" split the footer exists to prevent, and a silently
 * shortened `codeIds` is an obligation that stops drawing.
 */
const readCodeTaxonomySection = (
  raw: unknown,
): PortalCodeTaxonomySection | undefined => {
  if (!isObject(raw)) return undefined;
  const id = dualKey(raw, "id");
  if (typeof id !== "string" || !id) return undefined;
  const label = dualKey(raw, "label");
  const rawCodeIds = dualKey(raw, "code_ids");
  if (!Array.isArray(rawCodeIds)) return undefined;
  if (!rawCodeIds.every((v) => typeof v === "string")) return undefined;
  const total = readFinite(dualKey(raw, "total"));
  const applicable = readFinite(dualKey(raw, "applicable"));
  const projectedPenalty = readFinite(dualKey(raw, "projected_penalty"));
  if (total === undefined || applicable === undefined) return undefined;
  if (projectedPenalty === undefined) return undefined;
  return {
    id,
    label: typeof label === "string" ? label : "",
    codeIds: rawCodeIds as string[],
    total,
    applicable,
    projectedPenalty,
  };
};

/**
 * Read the `code_taxonomy` block.
 *
 * ALL-OR-NOTHING, DELIBERATELY — AND THE STRUCTURE COUNTS, NOT JUST THE
 * THREE BLOCK FIGURES. Returns `undefined` unless the block carries a
 * NON-EMPTY array of well-formed categories AND all three block figures are
 * finite numbers. A half-read block is worse than an absent one: the
 * board's footer would state a rollup built partly from the wire and partly
 * from a default, which is precisely the fabricated figure this whole lane
 * exists to stop. Absent → the board falls back to the `CODE_GROUPS`
 * vocabulary and states no counts at all.
 *
 * THE HALF THAT CARRIES THE OBLIGATIONS IS THE HALF WORTH CHECKING. An
 * earlier cut guarded only the block figures, so `categories: []` — or an
 * array whose entries were not objects — parsed happily and drew a board
 * with zero sections under a confident "13 OF 33 REQUIRED · EST. $19,650":
 * every code gone from the page, the number over them still stated.
 * `build_code_taxonomy` always emits the four `CATEGORY_ORDER` sections, so
 * neither shape can come from today's server — which is the reason to
 * refuse them rather than render them.
 *
 * NO KEY REWRITING ANYWHERE INSIDE. `dualKey` covers the snake/camel field
 * names; the category `id`s (`dob_safety`) and every entry of `codeIds`
 * (`hpd_registration`) are string VALUES, which no camelizer has ever
 * touched — unlike `codes` / `penaltyPerCode`, whose code ids are KEYS and
 * need `readCodeKeyedRecord`.
 */
const readCodeTaxonomy = (raw: unknown): PortalCodeTaxonomy | undefined => {
  if (!isObject(raw)) return undefined;
  const rawCategories = dualKey(raw, "categories");
  if (!Array.isArray(rawCategories) || rawCategories.length === 0) {
    return undefined;
  }
  const total = readFinite(dualKey(raw, "total"));
  const applicable = readFinite(dualKey(raw, "applicable"));
  const projectedPenalty = readFinite(dualKey(raw, "projected_penalty"));
  if (total === undefined || applicable === undefined) return undefined;
  if (projectedPenalty === undefined) return undefined;
  const categories: PortalCodeTaxonomySection[] = [];
  for (const entry of rawCategories) {
    const section = readCodeTaxonomySection(entry);
    // One malformed section rejects the BLOCK. Dropping it instead would
    // leave the block figures describing sections that are no longer on
    // screen — the same fabricated rollup, one section at a time.
    if (!section) return undefined;
    categories.push(section);
  }
  return {
    categories,
    total,
    applicable,
    projectedPenalty,
    uncategorized: readIdList(dualKey(raw, "uncategorized")),
    missing: readIdList(dualKey(raw, "missing")),
  };
};

/** All four buckets or none: a partial set would mix wire and default. */
const readBucketCounts = (raw: unknown): PortalCodeBucketCounts | undefined => {
  if (!isObject(raw)) return undefined;
  const eligible = readFinite(dualKey(raw, "eligible"));
  const maybe = readFinite(dualKey(raw, "maybe"));
  const unknown = readFinite(dualKey(raw, "unknown"));
  const notEligible = readFinite(dualKey(raw, "not_eligible"));
  if (
    eligible === undefined ||
    maybe === undefined ||
    unknown === undefined ||
    notEligible === undefined
  ) {
    return undefined;
  }
  return { eligible, maybe, unknown, notEligible };
};

/**
 * Codes payload — the key-spelling rules here are the whole reason this
 * endpoint was MED-hazard:
 *
 *   - ``codes`` / ``penaltyPerCode`` / ``confidenceBand`` record KEYS
 *     are code IDs ("hpd_registration", "elevator_cat1") and must stay
 *     snake_case verbatim to match ``CODE_GROUPS``.
 *   - ``codes`` entry VALUES follow the camelCase ``CodeResult``
 *     contract (``needsOwnerInput``, ``actionState``, ``subcycle``…) —
 *     historically only the TOP-level keys were restored to snake, so
 *     the schema camelizes the inner values via ``toCamelDeep``.
 *   - ``confidenceBand`` values are the snake_case
 *     ``ConfidenceBandShape`` (``confidence_label`` / ``cure_window``)
 *     — see the field's doc on ``PortalBuildingCodesData`` — so the
 *     whole sub-tree rides the digit-safe ``decamelizeKeysDeep``
 *     instead.
 *
 * THIS TRANSFORM IS A WHITELIST: it BUILDS its result from named fields
 * rather than spreading the payload, so a wire field nobody names here is
 * dropped at the boundary and no component can ever read it. That is the
 * point (typed ownership) and it is also the trap — ``code_taxonomy``
 * shipped on the wire at commit a173a6e3 and reached nothing for a week
 * because no line here named it. If you are adding a BE field and it is
 * arriving ``undefined`` in a component, this is why.
 */
export const portalBuildingCodesSchema: z.ZodType<
  PortalBuildingCodesData,
  z.ZodTypeDef,
  unknown
> = z.unknown().transform((input): PortalBuildingCodesData => {
  const raw = isObject(input) ? input : {};
  const lineage = dualKey(raw, "data_lineage");
  const confidenceBand = dualKey(raw, "confidence_band");
  const penaltyBreakdown = dualKey(raw, "penalty_breakdown");
  const taxonomy = dualKey(raw, "code_taxonomy");
  return {
    ...readBuildingMetrics(raw),
    codes:
      readCodeKeyedRecord(
        dualKey(raw, "codes"),
        (v) => toCamelDeep(v) as CodeResult,
      ) ?? {},
    penaltyPerCode:
      readCodeKeyedRecord(
        dualKey(raw, "penalty_per_code"),
        (v) => v as number,
      ) ?? {},
    codeTaxonomy: readCodeTaxonomy(taxonomy),
    codeBucketCounts: isObject(taxonomy)
      ? readBucketCounts(dualKey(taxonomy, "bucket_counts"))
      : undefined,
    grossSqft: (dualKey(raw, "gross_sqft") ?? null) as number | null,
    numBuildings: (dualKey(raw, "num_buildings") ?? null) as number | null,
    borough: (dualKey(raw, "borough") ?? null) as string | null,
    bbl: (dualKey(raw, "bbl") ?? null) as string | null,
    ll97Submissions: readSubmissionList(dualKey(raw, "ll97_submissions")),
    ll88Submissions: readSubmissionList(dualKey(raw, "ll88_submissions")),
    ll87Submissions: readLL87SubmissionList(dualKey(raw, "ll87_submissions")),
    dataLineage: (isObject(lineage) ? toCamelDeep(lineage) : undefined) as
      | DataLineage
      | undefined,
    confidenceBand: (isObject(confidenceBand)
      ? decamelizeKeysDeep(confidenceBand)
      : undefined) as Record<string, unknown> | undefined,
    penaltyBreakdown: (isObject(penaltyBreakdown)
      ? decamelizeKeysDeep(penaltyBreakdown)
      : undefined) as Record<string, unknown> | undefined,
    penaltyExposureEstimated: dualKey(raw, "penalty_exposure_estimated") as
      | number
      | undefined,
    judgmentInterest: (isObject(dualKey(raw, "judgment_interest"))
      ? toCamelDeep(dualKey(raw, "judgment_interest"))
      : undefined) as PortalJudgmentInterest | undefined,
    enforcementByAgency: (isObject(dualKey(raw, "enforcement_by_agency"))
      ? toCamelDeep(dualKey(raw, "enforcement_by_agency"))
      : undefined) as PortalEnforcementByAgency | undefined,
    judgmentInterestByAgency: (isObject(
      dualKey(raw, "judgment_interest_by_agency"),
    )
      ? toCamelDeep(dualKey(raw, "judgment_interest_by_agency"))
      : undefined) as PortalBuildingCodesData["judgmentInterestByAgency"],
    bins: Array.isArray(dualKey(raw, "bins"))
      ? (dualKey(raw, "bins") as unknown[]).filter(
          (b): b is string => typeof b === "string",
        )
      : undefined,
  };
});

export const fetchPortalBuildingCodes = (
  buildingId: string,
  refresh?: boolean,
): Promise<PortalBuildingCodesData> =>
  codecGet(
    `tracking/portal/buildings/${buildingId}/codes/`,
    portalBuildingCodesSchema,
    { params: refresh ? { refresh: "1" } : undefined },
  );

// ── Code assignments (Portal V1.1 — server-side persistence) ───────────
//
// Replaces the old localStorage-backed `lib/codeAssignments.ts`. The
// state vocabulary intentionally collapses to three values that match
// the BE contract: `in_service` / `out` / `undecided`. Codes not
// explicitly in the response default to `undecided` on read.
//
// Codes NOT present in the response default to `undecided` — the BE
// only stores explicit assignments. Don't loop over every code ID
// client-side; `assignments[codeId] ?? "undecided"` at the call site
// is the contract.

export type CodeAssignmentState = "in_service" | "out" | "undecided";

export interface CodeAssignmentsResponse {
  assignments: Record<string, CodeAssignmentState>;
}

export interface CodeAssignmentPatchResponse {
  codeId: string;
  state: CodeAssignmentState;
}

// Same code-id-as-key concern as `codes` / `penaltyPerCode`: the keys
// of `assignments` are code IDs and must stay snake_case to match
// CODE_GROUPS. The values are enum STRINGS ("in_service") which no key
// rewrite ever touches.
export const codeAssignmentsSchema: z.ZodType<
  CodeAssignmentsResponse,
  z.ZodTypeDef,
  unknown
> = z.unknown().transform((input): CodeAssignmentsResponse => {
  const raw = isObject(input) ? input : {};
  return {
    assignments:
      readCodeKeyedRecord(
        dualKey(raw, "assignments"),
        (v) => v as CodeAssignmentState,
      ) ?? {},
  };
});

export const fetchCodeAssignments = (
  buildingId: string,
): Promise<CodeAssignmentsResponse> =>
  codecGet(
    `tracking/portal/buildings/${buildingId}/code-assignments/`,
    codeAssignmentsSchema,
  );

export const patchCodeAssignment = (
  buildingId: string,
  codeId: string,
  state: CodeAssignmentState,
): Promise<CodeAssignmentPatchResponse> =>
  api
    .patch(
      `tracking/portal/buildings/${buildingId}/code-assignments/${codeId}/`,
      { state },
    )
    .then((r) => r.data);

// ── Single-building scheduling (LL152 today; generic across booking-style
//    obligations as we add LL11/LL126/BPP_BOILER) ────────────────────────

export interface SingleBuildingSchedulingRequest {
  obligationCode: "LL152";
  vendorId: string;
  vendorName: string;
  slotDate: string; // ISO yyyy-mm-dd
  slotTime: string; // "8am" | "11am" | "2pm" etc.
  notes?: string;
  contact?: string;
}

export interface SingleBuildingSchedulingResponse {
  bookingId: string;
  actionState: CodeActionState;
}

export const postSingleBuildingScheduling = (
  buildingId: string,
  body: SingleBuildingSchedulingRequest,
): Promise<SingleBuildingSchedulingResponse> =>
  api
    .post(`tracking/portal/buildings/${buildingId}/scheduling/booking/`, {
      obligation_code: body.obligationCode,
      vendor_id: body.vendorId,
      vendor_name: body.vendorName,
      slot_date: body.slotDate,
      slot_time: body.slotTime,
      notes: body.notes ?? "",
      contact: body.contact ?? "",
    })
    .then((r) => r.data);

// ---------------------------------------------------------------------------
// Wave-8 Cluster B — portfolio aggregate endpoints.
//
// The three composers below mirror the wire shape documented in
// ``backend/server/tracking/services_portfolio_aggregates.py``. Decimal
// amounts ride the wire as strings so axios' camelize interceptor does
// not destroy precision; the FE coerces via ``Number(...)`` at the
// render site (see PortfolioHandledWins).
//
// Cross-company isolation is enforced server-side; the FE never
// passes a company_id or user_id query param.
// ---------------------------------------------------------------------------

export interface PortalPortfolioSummary {
  asOf: string | null;
  openViolations: number;
  deadlines90d: number;
  /**
   * WHAT THE 90-DAY COUNT IS ACTUALLY MADE OF. Declared because the figure
   * alone cannot be labelled honestly: on the measured portfolio this is
   * `{filing: 0, cure: 0, permit: 5}`, so the counter that printed "FILING
   * DEADLINE · 90D" over it was naming a bucket with nothing in it. The FE
   * spells the contents out (`deadlineSplitNote`) instead of guessing.
   *
   * Optional — older payloads ship the total and no split, which renders the
   * figure with no contents line rather than an invented one.
   */
  deadlinesByType?: {
    filing?: number;
    cure?: number;
    permit?: number;
  };
  /**
   * WHAT THE 90-DAY COUNT DOES NOT COVER — the BE's own honesty flags, shipped
   * on the SAME response as `deadlines_90d` and, until this fix, read by
   * nothing.
   *
   * THIS IS THE `resolved_meta` TEST, APPLIED TO THE COUNTER WE KEPT. The tile
   * that printed "0 CLEARED YTD" was deleted because `cleared_ytd` arrives
   * beside three deferred detectors and therefore measures a missing feed
   * rather than an absence of resolutions. `deadlines_by_type.cure` is the
   * same shape: `services_portfolio_aggregates.py:226` initialises
   * `cure_90d = 0` and NEVER increments it — Tier 2 (cure / certify-by, BEAM
   * 14-day audit clocks) is structurally unmeasured, and the BE says so in
   * `tier2_cure_deferred` rather than "silently emitting a confident 0 as if
   * the tier were counted" (its words, same file). A FE that reads the split
   * and ignores the flag re-commits the defect one tile to the left, quietly.
   *
   * `overdueExcludedFromDeadlines` matters for the opposite reason: a
   * portfolio whose deadlines are ALL already overdue reads `0` here while
   * `overdueCount` beside it is non-zero. The figure is a forward window, not
   * a workload.
   *
   * Optional throughout — a payload that predates the delta ships no meta, and
   * an absent flag is NOT a false one: `deadlineScopeNote` states only the
   * caveats the BE actually raised.
   */
  deadlinesMeta?: {
    /** Overdue work is counted separately, never inside `deadlines90d`. */
    overdueExcludedFromDeadlines?: boolean;
    /** Tier 2 cure / certify-by clocks are not sourced yet — not "zero". */
    tier2CureDeferred?: boolean;
    /** jz4z-kudi surfaces a hearing only once docketed — FDNY is a floor. */
    hearingsFdnyPreHearingGap?: boolean;
  };
  /**
   * Deadlines already PAST, deliberately outside `deadlines90d`. One formula
   * with the per-building `overdue_filings_count` (BE `classify_code_deadlines`).
   */
  overdueCount?: number;
  /** OATH hearings scheduled within 90d — adjacent, never blended (D4). */
  hearings90d?: number;
  /**
   * `cleared_ytd` IS NOT A COUNT OF THINGS CLEARED, and no surface renders it.
   *
   * The BE ships it beside `resolved_meta`, which on the measured portfolio
   * reports `oath_paid_detection_deferred`, `dob_now_resolved_deferred` AND
   * `hpd_excluded_from_resolved` — three of three resolution detectors either
   * deferred or excluded — while `hpd_cured_ytd` on the same payload is 574.
   * So the 0 measures the absence of a resolution feed, not an absence of
   * resolutions, and `PortfolioHandledWins` proves it by listing HPD cures from
   * the last week. PR-17 deleted the tile that printed it (absent is not zero).
   * The field stays declared so the next reader finds this note before
   * rendering it again; render `hpdCuredYtd` under an honest label instead.
   */
  clearedYtd: number;
  /** HPD cures YTD — real, and the reason `clearedYtd: 0` is not a fact. */
  hpdCuredYtd?: number;
}

export interface PortalPortfolioWin {
  what: string;
  /** Decimal string — preserves BE precision through axios camelize. */
  amountClearedUsd: string;
  /** Human-relative phrase ("2 weeks ago" / "Apr 30") composed BE-side. */
  when: string;
  buildingName: string;
  buildingId: string;
}

export interface PortalPortfolioWinsResponse {
  asOf: string | null;
  wins: PortalPortfolioWin[];
}

export type PortfolioDriverSeverity = "high" | "med";

export interface PortalPortfolioDriver {
  sev: PortfolioDriverSeverity;
  what: string;
  where: string;
  cost: string;
  cta: string;
  buildingId: string;
}

export interface PortalPortfolioDriversResponse {
  asOf: string | null;
  drivers: PortalPortfolioDriver[];
}

export const fetchPortalPortfolioSummary =
  (): Promise<PortalPortfolioSummary> =>
    api.get("tracking/portal/portfolio/summary/").then((r) => r.data);

export const fetchPortalPortfolioWins = (
  limit: number = 3,
): Promise<PortalPortfolioWinsResponse> =>
  api
    .get("tracking/portal/portfolio/wins/", { params: { limit } })
    .then((r) => r.data);

export const fetchPortalPortfolioDrivers = (
  limit: number = 3,
): Promise<PortalPortfolioDriversResponse> =>
  api
    .get("tracking/portal/portfolio/drivers/", { params: { limit } })
    .then((r) => r.data);

export interface HardDeleteBuildingRequest {
  /** Exact ``address1`` echo. Server compares case-insensitively. */
  addressTyped: string;
  /**
   * Client-generated idempotency key (UUID v4 works). Server caches
   * the response under (user_id, key) for 5 min so a network retry
   * of the same gesture returns the original 200 — not a 404 from
   * "the row is genuinely gone now."
   */
  idempotencyKey: string;
}

export interface HardDeleteBuildingResponse {
  status: "deleted";
  buildingId: string;
}

export const postHardDeleteBuilding = (
  buildingId: string,
  body: HardDeleteBuildingRequest,
): Promise<HardDeleteBuildingResponse> =>
  api
    .post(
      `tracking/portal/buildings/${buildingId}/hard-delete/`,
      {
        confirm: true,
        address_typed: body.addressTyped,
      },
      {
        headers: {
          "Idempotency-Key": body.idempotencyKey,
        },
      },
    )
    .then((r) => r.data);

// ---------------------------------------------------------------------------
// Path-A login warm-up — portfolio warm-up status (Stream E).
//
// Single endpoint, LAZY-TRIGGER on GET: the FIRST GET dispatches
// ``warmup_portfolio(company_id, kind="warmup")`` server-side IFF the
// portfolio has cold/stale buildings AND the per-company Redis lock is
// free; later polls are pure reads. There is NO separate /start endpoint.
//
// The Welcome splash polls this until ``ready`` (or a client-side
// max-wait guard fires), then routes the user to /portal.
//
// Wire shape is snake_case on the BE (``as_of``, ``building_count``,
// ``max_wait_exceeded``). Since the Phase 3 codec migration the fetch
// rides ``codecGet`` (camelizer skipped) and ``normalizeWarmupStatus``
// reads both spellings via ``dualKey``, emitting the camelCase names
// below (``asOf`` / ``buildingCount`` / ``maxWaitExceeded``). The
// ``step.state`` VALUES ("done"/"running"/"pending"/"error") are never
// key-rewritten on any path (see ``feedback_camelcase_axios_jsonfield``).
//
// Progress is REAL: ``percent`` is derived BE-side from snapshot
// freshness (``portal_views._build_dashboard_freshness``), never a
// synthetic client timer. ``exp`` / ``due`` step details are ``null``
// until those source groups are actually fresh — the FE must NEVER
// render a $ figure or deadline over cold/partial data.
// ---------------------------------------------------------------------------

export type WarmupStepState = "pending" | "running" | "done" | "error";

export interface WarmupStep {
  id: string;
  state: WarmupStepState;
  detail?: string | null;
}

export interface WarmupStatus {
  /** true => the FE may route to /portal. Latches on either real
   * coverage OR the BE's ~10s max-wait budget — see ``fullyReady`` for
   * the stricter signal. */
  ready: boolean;
  /** true => every source group is honestly fresh (rec + exp + due).
   * Distinct from ``ready`` in that it does NOT latch on the max-wait
   * budget. The splash polls until this flips so a portfolio that
   * finishes warming AFTER the budget blew still auto-advances cleanly
   * instead of stranding the user on the escape button. */
  fullyReady: boolean;
  /** Overall 0-100, derived from REAL snapshot freshness BE-side. */
  percent: number;
  /** MAX(fetched_at) across portfolio snapshots; null when none exist. */
  asOf: string | null;
  buildingCount: number;
  steps: WarmupStep[];
  /** true once the server's ~10s max-wait elapsed (ready is also true). */
  maxWaitExceeded: boolean;
}

const WARMUP_STEP_STATES: ReadonlySet<WarmupStepState> = new Set([
  "pending",
  "running",
  "done",
  "error",
]);

/**
 * Coerce ``step.state`` to the 4-value union. The camelizer in
 * ``api/base`` does NOT touch these enum VALUES, but a BE drift / typo /
 * future-state-we-don't-know-yet could send an unexpected string. If we
 * let that fall through the component's ``switch(state)``, an unmatched
 * branch can dump a raw object as a React child ("Objects are not valid
 * as a React child" — the camelize landmine). Map anything unknown to
 * ``"running"`` so the splash keeps spinning rather than crashing.
 */
export function normalizeWarmupStatus(raw: unknown): WarmupStatus {
  const r = isObject(raw) ? raw : {};
  const rawSteps = dualKey(r, "steps");

  const steps: WarmupStep[] = (Array.isArray(rawSteps) ? rawSteps : []).map(
    (s) => {
      const step = isObject(s) ? s : {};
      const state = dualKey(step, "state");
      const safeState: WarmupStepState =
        typeof state === "string" &&
        WARMUP_STEP_STATES.has(state as WarmupStepState)
          ? (state as WarmupStepState)
          : "running";
      const id = dualKey(step, "id");
      const detail = dualKey(step, "detail");
      return {
        id: typeof id === "string" ? id : "",
        state: safeState,
        detail:
          typeof detail === "string" || detail === null
            ? (detail as string | null)
            : null,
      };
    },
  );

  const percent = dualKey(r, "percent");
  const asOf = dualKey(r, "as_of");
  const buildingCount = dualKey(r, "building_count");

  return {
    // Strict-boolean coercion — pinned by the 12 WelcomeWarmup splash
    // tests. Anything that isn't literally `true` (including truthy
    // junk like "true" or 1) reads as false: the splash must NEVER
    // advance on a malformed payload.
    ready: dualKey(r, "ready") === true,
    fullyReady: dualKey(r, "fully_ready") === true,
    percent:
      typeof percent === "number" && Number.isFinite(percent)
        ? Math.max(0, Math.min(100, Math.round(percent)))
        : 0,
    asOf: typeof asOf === "string" ? asOf : null,
    buildingCount:
      typeof buildingCount === "number" && Number.isFinite(buildingCount)
        ? buildingCount
        : 0,
    steps,
    maxWaitExceeded: dualKey(r, "max_wait_exceeded") === true,
  };
}

/**
 * Phase 3 codec — `normalizeWarmupStatus` IS the schema body. Kept as a
 * named export (the WelcomeWarmup spec builds its fixtures through it)
 * and wrapped in a Zod transform so `fetchWarmupStatus` rides the same
 * `codecGet` boundary as every other portal read.
 */
export const warmupStatusSchema: z.ZodType<
  WarmupStatus,
  z.ZodTypeDef,
  unknown
> = z.unknown().transform(normalizeWarmupStatus);

export const fetchWarmupStatus = (): Promise<WarmupStatus> =>
  codecGet("tracking/portal/warmup/status/", warmupStatusSchema);
